/**
 * Department Management Component
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, Building2, 
  Loader, X, Save, Users, Lock 
} from 'lucide-react';

const DepartmentManagement = () => {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [editDepartmentName, setEditDepartmentName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [seedingDefaults, setSeedingDefaults] = useState(false);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/departments/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setDepartments(response.data || []);
    } catch (err) {
      console.error('Error loading departments:', err);
      setError('Departmanlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      setError('Lütfen departman adı girin');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.post(
        `${API}/api/departments/`,
        { name: newDepartmentName },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setShowAddModal(false);
      setNewDepartmentName('');
      loadDepartments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Departman eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleEditDepartment = async () => {
    if (!editDepartmentName.trim()) {
      setError('Lütfen departman adı girin');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.put(
        `${API}/api/departments/${selectedDepartment.id}`,
        { name: editDepartmentName },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setShowEditModal(false);
      setSelectedDepartment(null);
      setEditDepartmentName('');
      loadDepartments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Departman güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async () => {
    setSaving(true);
    setError('');

    try {
      await axios.delete(
        `${API}/api/departments/${selectedDepartment.id}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      setShowDeleteModal(false);
      setSelectedDepartment(null);
      loadDepartments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Departman silinemedi');
      setSaving(false);
    } finally {
      if (!error) setSaving(false);
    }
  };

  const handleSeedDefaultDepartments = async () => {
    setSeedingDefaults(true);
    setError('');

    const defaultDepartments = [
      "Genel Müdürlük", "İdari İşler", "Finans", "Satınalma",
      "Pazarlama", "Satış", "IT", "İnsan Kaynakları",
      "Operasyon", "Teknik", "Hukuk"
    ];

    try {
      let successCount = 0;
      for (const name of defaultDepartments) {
        try {
          await axios.post(
            `${API}/api/departments/`,
            { name },
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          );
          successCount++;
        } catch (err) {
          // Ignore duplicates
          if (!err.response?.data?.detail?.includes('zaten mevcut')) {
            console.error(`Error adding ${name}:`, err);
          }
        }
      }

      if (successCount > 0) {
        alert(`${successCount} varsayılan departman başarıyla eklendi!`);
        loadDepartments();
      } else {
        alert('Tüm varsayılan departmanlar zaten mevcut.');
      }
    } catch (err) {
      setError('Varsayılan departmanlar eklenirken hata oluştu');
    } finally {
      setSeedingDefaults(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Departman Yönetimi</h2>
          <p className="text-gray-600 mt-1">
            Şirket departmanlarınızı yönetin. Sistem varsayılan departmanları (mor)
            tüm şirketlerde geçerlidir ve yalnızca Ultra Admin tarafından düzenlenebilir.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-5 h-5" />
          Yeni Departman
        </button>
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((dept) => {
          const isGlobal = dept.is_default === true || dept.company_id === 'global';
          return (
            <div
              key={dept.id}
              className={`bg-white border rounded-lg p-4 hover:shadow-md transition ${
                isGlobal ? 'border-gray-200 bg-gray-50/40' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isGlobal ? 'bg-violet-100' : 'bg-blue-100'
                  }`}>
                    <Building2 className={`w-5 h-5 ${
                      isGlobal ? 'text-violet-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 truncate">
                      {dept.name}
                    </h3>
                    {isGlobal && (
                      <span
                        className="inline-flex items-center text-xs text-violet-700 bg-violet-100 px-2 py-0.5 rounded gap-1 mt-1"
                        title="Sistem genelinde tanımlı — sadece Ultra Admin tarafından düzenlenebilir"
                      >
                        <Lock className="w-3 h-3" />
                        Varsayılan (Sistem)
                      </span>
                    )}
                  </div>
                </div>
                {!isGlobal && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setSelectedDepartment(dept);
                        setEditDepartmentName(dept.name);
                        setShowEditModal(true);
                      }}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
                      title="Düzenle"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedDepartment(dept);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                      title="Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {departments.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">Henüz şirkete özel departman eklenmemiş</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Departman Ekle
            </button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Yeni Departman Ekle</h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departman Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value)}
                  placeholder="Örn: Satış"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={saving}
                  autoFocus
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewDepartmentName('');
                  setError('');
                }}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleAddDepartment}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Departmanı Düzenle</h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departman Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editDepartmentName}
                  onChange={(e) => setEditDepartmentName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={saving}
                  autoFocus
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDepartment(null);
                  setEditDepartmentName('');
                  setError('');
                }}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleEditDepartment}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Güncelle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Departmanı Sil</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <p className="text-gray-600 mb-6">
              <strong>{selectedDepartment?.name}</strong> departmanını silmek istediğinizden emin misiniz? 
              Bu işlem geri alınamaz.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDepartment(null);
                  setError('');
                }}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteDepartment}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-300 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
